import type { AbilityDef, Content, Modifier, PlayerBaseStats, StatId } from '@gra/content';

export type PlayerStats = PlayerBaseStats;

export interface AbilityStats {
  damage: number;
  cooldown: number;
  projectileCount: number;
  projectileSpeed: number;
  pierce: number;
  area: number;
  range: number;
  /** Szerokość łuku ciosu wręcz w radianach. */
  arc: number;
  knockback: number;
}

/**
 * (baza + suma "add") * (1 + suma "mul").
 * Modyfikatory z tagami liczą się tylko dla umiejętności, które mają wszystkie te tagi.
 */
export function applyModifiers(
  base: number,
  stat: StatId,
  mods: readonly Modifier[],
  abilityTags?: readonly string[],
): number {
  let add = 0;
  let mul = 0;
  for (const m of mods) {
    if (m.stat !== stat) continue;
    if (m.tags && m.tags.length > 0) {
      if (!abilityTags || !m.tags.every((t) => abilityTags.includes(t))) continue;
    }
    if (m.op === 'add') add += m.value;
    else mul += m.value;
  }
  return (base + add) * (1 + mul);
}

export function computePlayerStats(base: PlayerBaseStats, mods: readonly Modifier[]): PlayerStats {
  return {
    maxHealth: applyModifiers(base.maxHealth, 'maxHealth', mods),
    moveSpeed: applyModifiers(base.moveSpeed, 'moveSpeed', mods),
    pickupRadius: applyModifiers(base.pickupRadius, 'pickupRadius', mods),
    critChance: Math.min(1, Math.max(0, applyModifiers(base.critChance, 'critChance', mods))),
    armor: Math.max(0, applyModifiers(base.armor, 'armor', mods)),
    regen: Math.max(0, applyModifiers(base.regen, 'regen', mods)),
  };
}

/**
 * Premia za rangę umiejętności (ulepszenia z awansów): trochę więcej obrażeń, krótszy czas odnowienia i zasięg;
 * pociski dostają jeden dodatkowy pocisk na randze 5.
 */
export function rankModifiers(def: AbilityDef, rank: number): Modifier[] {
  const r = rank - 1;
  if (r <= 0) return [];
  const mods: Modifier[] = [
    { stat: 'damage', op: 'mul', value: 0.1 * r },
    { stat: 'cooldown', op: 'mul', value: -0.03 * r },
    { stat: 'area', op: 'mul', value: 0.03 * r },
  ];
  if (def.kind === 'projectile' && r >= 4) mods.push({ stat: 'projectileCount', op: 'add', value: 1 });
  return mods;
}

export function computeAbilityStats(def: AbilityDef, baseMods: readonly Modifier[], rank = 1): AbilityStats {
  const t = def.tags;
  const mods = rank > 1 ? [...baseMods, ...rankModifiers(def, rank)] : baseMods;
  return {
    damage: applyModifiers(def.damage, 'damage', mods, t),
    cooldown: Math.max(0.1, applyModifiers(def.cooldown, 'cooldown', mods, t)),
    projectileCount: Math.max(1, Math.round(applyModifiers(def.projectileCount, 'projectileCount', mods, t))),
    projectileSpeed: applyModifiers(def.projectileSpeed, 'projectileSpeed', mods, t),
    pierce: Math.max(0, Math.round(applyModifiers(def.pierce, 'pierce', mods, t))),
    area: applyModifiers(def.area, 'area', mods, t),
    range: applyModifiers(def.range, 'range', mods, t),
    arc: (def.arc * Math.PI) / 180,
    knockback: def.knockback,
  };
}

/** Spłaszcza talenty do listy modyfikatorów (wartość * ranga). */
export function collectModifiers(content: Content, talents: Readonly<Record<string, number>>): Modifier[] {
  const out: Modifier[] = [];
  for (const [id, rank] of Object.entries(talents)) {
    const talent = content.talents[id];
    if (!talent || rank <= 0) continue;
    for (const m of talent.modifiers) out.push({ ...m, value: m.value * rank });
  }
  return out;
}
